import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { ArrowLeft, FileText, User, Scale, Sparkles, AlertTriangle, ShieldCheck, Stethoscope } from 'lucide-react';
import { motion } from 'framer-motion';

export default function TermsPage() {
  const lastUpdated = "March 2026";

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-primary">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
          <Logo size="default" linkTo="/" />
          <div className="w-16 sm:w-20" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A]">Terms & Conditions</h1>
          </div>
          <p className="text-muted-foreground mb-6 sm:mb-8">Last Updated: {lastUpdated}</p>

          <div className="prose prose-lg max-w-none space-y-6 sm:space-y-8">
            {/* User Eligibility */}
            <section className="bg-white rounded-xl p-4 sm:p-6 border border-border">
              <div className="flex items-center gap-3 mb-4">
                <User className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0" />
                <h2 className="text-lg sm:text-xl font-semibold text-[#1A1A1A] m-0">User Eligibility</h2>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4">
                <div className="flex items-start gap-2 sm:gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-amber-800 font-medium text-sm sm:text-base mb-0">
                    Age Requirement: You must be at least <strong>18 years of age</strong> to use Vitala.me.
                  </p>
                </div>
              </div>
            </section>

            {/* Service Type */}
            <section className="bg-white rounded-xl p-4 sm:p-6 border border-border">
              <div className="flex items-center gap-3 mb-4">
                <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0" />
                <h2 className="text-lg sm:text-xl font-semibold text-[#1A1A1A] m-0">Service Type</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed text-sm sm:text-base">
                Vitala.me is currently a <strong>free informational service</strong>. No fees are charged for our AI assessments.
              </p>
            </section>

            {/* Nature of AI Suggestions */}
            <section className="bg-white rounded-xl p-4 sm:p-6 border border-border">
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0" />
                <h2 className="text-lg sm:text-xl font-semibold text-[#1A1A1A] m-0">Nature of AI Suggestions</h2>
              </div>
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                  <p className="font-semibold text-blue-800 mb-2 text-sm sm:text-base">Experimental Content</p>
                  <p className="text-blue-700 text-xs sm:text-sm mb-0">
                    You acknowledge that Vitala.me utilizes Artificial Intelligence which may occasionally 
                    produce inaccuracies ("hallucinations").
                  </p>
                </div>
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
                  <p className="font-semibold text-red-800 mb-2 text-sm sm:text-base">Assumption of Risk</p>
                  <p className="text-red-700 text-xs sm:text-sm mb-3">
                    All supplement protocols are <strong>suggestions, not prescriptions</strong>. By using this site, 
                    you agree that Vitala.me is not liable for any adverse reactions or outcomes resulting 
                    from the use of suggested supplements.
                  </p>
                  <div className="flex items-start gap-2 pt-2 border-t border-red-200">
                    <Stethoscope className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-red-700 text-xs sm:text-sm mb-0 font-medium">
                      Always consult your healthcare professional before starting any supplement regimen.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Limitation of Liability */}
            <section className="bg-white rounded-xl p-4 sm:p-6 border border-border">
              <div className="flex items-center gap-3 mb-4">
                <Scale className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0" />
                <h2 className="text-lg sm:text-xl font-semibold text-[#1A1A1A] m-0">Limitation of Liability</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed text-sm sm:text-base">
                Vitala.me and its parent platform (Emergent) are not responsible for how you interpret 
                or apply the AI-generated suggestions. <strong>Always cross-reference AI data with professional 
                medical advice.</strong>
              </p>
            </section>

            {/* Contact */}
            <section className="bg-primary/5 border border-primary/20 rounded-xl p-4 sm:p-6">
              <p className="text-[#1A1A1A] text-sm sm:text-base mb-0">
                Questions about these Terms? Contact us at:{' '}
                <a href="mailto:support@vitala.me" className="text-primary hover:underline font-medium">
                  support@vitala.me
                </a>
              </p>
            </section>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
